
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // ✅ Add this import
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import Hero from '../components/Hero/Hero';
import ProductsGrid from '../components/Products/ProductsGrid';
import { productService } from '../services/api';
import QuickViewModal from '../components/Products/QuickViewModal';

const Home = () => {
  const [sortBy, setSortBy] = useState('newest');
  const [displayCount, setDisplayCount] = useState(36);
  const [viewMode, setViewMode] = useState('grid');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);

  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const navigate = useNavigate(); // ✅ Create navigate function

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchProducts();
    return () => {
      mountedRef.current = false;
    };
  }, [sortBy, displayCount]);

  // KEEP YOUR EXISTING fetchProducts FUNCTION EXACTLY AS IT WAS
  const fetchProducts = async () => {
    try {
      setLoading(true);

      // Map frontend sort options to backend ordering
      let backendOrdering = '-created_at';
      if (sortBy === 'price-low') backendOrdering = 'price';
      if (sortBy === 'price-high') backendOrdering = '-price';
      if (sortBy === 'name') backendOrdering = 'name';
      if (sortBy === 'brand') backendOrdering = 'brand__name';

      const response = await productService.getProducts({
        ordering: backendOrdering,
        page_size: displayCount,
      });

      const results = response.results ?? response;
      const count = response.count ?? (Array.isArray(results) ? results.length : 0);

      const processedProducts = (results || []).map(product => {
        // PythonAnywhere API returns:
        // - primary_image: full URL (https://kingsolarman.pythonanywhere.com/media/products/...)
        // - current_price: string (already correct price)
        // - has_discount: boolean
        // - discount_percentage: number
        // - brand: object with name property
        // - NO price field, NO discount_price field, NO description field, NO images array
        
        const primaryImage = product.primary_image || null;
        const brandName = product.brand?.name || '';
        
        // For list view, current_price is the price to display
        const currentPrice = Number(product.current_price) || 0;
        const hasDiscount = !!product.has_discount;
        const discountPercentage = product.discount_percentage || 0;
        
        // Calculate original price if there's a discount
        let originalPrice = currentPrice;
        if (hasDiscount && discountPercentage > 0) {
          originalPrice = currentPrice / (1 - discountPercentage / 100);
        }

        return {
          id: product.id,
          name: product.name,
          productname: product.name,
          sku: product.sku,
          brand: brandName,
          // For display: original price (calculated or same as current)
          price: originalPrice,
          // Discount price is current price if discounted
          discountprice: hasDiscount ? currentPrice : 0,
          current_price: currentPrice,
          has_discount: hasDiscount,
          discount_percentage: discountPercentage,
          stock_status: product.stock_status,
          stock: product.stock_status === 'in_stock' ? 'In Stock' : 'Out of Stock',
          category: product.category,
          description: '', // Not available in list view - will need detail API call
          productinfo: '', // Not available in list view - will need detail API call
          // primary_image already contains full PythonAnywhere URL
          image: primaryImage || '/images/placeholder.jpg',
          additionalImages: [], // Not available in list view - will need detail API call
          // Store original product for detail fetch if needed
          _original: product
        };
      });

      if (mountedRef.current) {
        setProducts(processedProducts);
        setTotalProducts(count);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      if (mountedRef.current) {
        setError('Failed to load products from server');
        setProducts([]);
        setTotalProducts(0);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // KEEP YOUR EXISTING formatImageUrl FUNCTION
  const formatImageUrl = (imagePath) => {
    if (!imagePath) return '/images/placeholder.jpg';
    // If it's already a full URL (from PythonAnywhere), return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it starts with /, assume it's relative to current domain
    if (imagePath.startsWith('/')) return imagePath;
    // Default fallback
    return '/images/placeholder.jpg';
  };

  // KEEP YOUR EXISTING handleQuickView FUNCTION
  const handleQuickView = async (product) => {
    try {
      console.log('Fetching detailed product for ID:', product.id);
      
      // Fetch full product details using the detail endpoint
      const detailedProduct = await productService.getProduct(product.id);
      
      console.log('Detailed product response received');
      
      // Process images correctly based on the API response structure
      const primaryImageObj = detailedProduct.primary_image;
      const imagesArray = detailedProduct.images || [];
      
      console.log('Primary image object:', primaryImageObj);
      console.log('Images array:', imagesArray);
      console.log('Images array length:', imagesArray.length);
      
      // Extract primary image URL
      const primaryImageUrl = primaryImageObj?.image_url || 
                            product.image || 
                            '/images/placeholder.jpg';
      
      // Extract additional images (excluding the primary one if it's in the array)
      let additionalImages = [];
      
      if (Array.isArray(imagesArray) && imagesArray.length > 0) {
        // Get all image URLs
        const allImageUrls = imagesArray
          .filter(img => img && img.image_url) // Filter out null/undefined
          .map(img => img.image_url); // Extract image_url
          
        console.log('All image URLs:', allImageUrls);
        
        // Remove primary image from additional images to avoid duplicates
        additionalImages = allImageUrls.filter(url => url !== primaryImageUrl);
        
        console.log('Additional images (without primary):', additionalImages);
      }
      
      // Enhanced product with full details
      const enhancedProduct = {
        ...product,
        // Update with detailed info
        description: detailedProduct.description || product.description || '',
        productinfo: detailedProduct.description || product.productinfo || '',
        // Use the correct primary image
        image: primaryImageUrl,
        additionalImages: additionalImages,
        // Add specification fields
        warranty: detailedProduct.warranty || '',
        power: detailedProduct.power || '',
        voltage: detailedProduct.voltage || '',
        efficiency: detailedProduct.efficiency || '',
        capacity: detailedProduct.capacity || '',
        cycles: detailedProduct.cycles || '',
        model: detailedProduct.model || '',
        // Price details - IMPORTANT: Use the detailed product prices
        price: Number(detailedProduct.price) || product.price,
        discountprice: Number(detailedProduct.discount_price) || product.discountprice,
        current_price: Number(detailedProduct.current_price) || product.current_price,
        has_discount: detailedProduct.has_discount || product.has_discount,
        discount_percentage: detailedProduct.discount_percentage || product.discount_percentage,
      };
      
      console.log('Enhanced product for QuickView:', enhancedProduct);
      console.log('Primary image:', enhancedProduct.image);
      console.log('Additional images count:', enhancedProduct.additionalImages.length);
      console.log('Additional images:', enhancedProduct.additionalImages);
      
      setQuickViewProduct(enhancedProduct);
      setIsModalOpen(true);
      document.body.style.overflow = 'hidden';
    } catch (error) {
      console.error('Error fetching product details:', error);
      console.log('Error details:', error.response || error.message);
      
      // Fallback to basic product data
      console.log('Using fallback product data');
      setQuickViewProduct(product);
      setIsModalOpen(true);
      document.body.style.overflow = 'hidden';
    }
  };

  // KEEP YOUR EXISTING closeQuickView FUNCTION
  const closeQuickView = () => {
    setIsModalOpen(false);
    setQuickViewProduct(null);
    document.body.style.overflow = 'auto';
  };


  // KEEP YOUR EXISTING handleAddToCart FUNCTION
  const handleAddToCart = (product) => {
    const cartProduct = {
      id: product.id,
      name: product.productname || product.name,
      price: product.discountprice || product.price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.productinfo || product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category')
    };

    addToCart(cartProduct);
    alert(`${cartProduct.name} added to cart!`);
  };


  // KEEP YOUR EXISTING formatPrice FUNCTION
  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return `P ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return price || 'P 0.00';
  };

  // KEEP YOUR EXISTING getDiscountPercentage FUNCTION
  const getDiscountPercentage = (price, discountPrice) => {
    if (price && discountPrice && price > discountPrice) {
      return Math.round(((price - discountPrice) / price) * 100);
    }
    return 0;
  };


  // ✅ ADD WISHLIST HANDLER
  const handleWishlistToggle = (product, e) => {
    e.stopPropagation(); // Prevent product card click
    
    const wishlistProduct = {
      id: product.id,
      name: product.productname || product.name,
      price: product.discountprice || product.price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.productinfo || product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category')
    };

    toggleWishlist(wishlistProduct);
    
    // Show feedback
    const isCurrentlyInWishlist = isInWishlist(product.id);
    const message = isCurrentlyInWishlist 
      ? `${wishlistProduct.name} removed from wishlist!`
      : `${wishlistProduct.name} added to wishlist!`;
    
    // Simple visual feedback - you could enhance this with toast notifications
    alert(message);
  };

  // ✅ ADD THIS SIMPLE NAVIGATION FUNCTION
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const displayedProducts = products.slice(0, displayCount);

  if (loading && products.length === 0) {
    return (
      <div className="home">
        <Hero />
        <div className="container">
          <div>
            <div></div>
            <p>Loading products from King Solarman...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="home">
        <Hero />
        <div className="container">
          <div className="error-message">
            <h3>Connection Error</h3>
            <p>{error}</p>
            <button onClick={fetchProducts} className="retry-btn">Retry Connection</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      <Hero />
      <ProductsGrid />

      <section className="product-catalog-home">
        <div className="container">
          <div className="results-header">
            <div className="results-info">
              <span>
                Showing {Math.min(displayCount, products.length)} of {totalProducts} products
                {error && ' (connection issues)'}
              </span>
            </div>
            <div className="results-controls">
              <div className="control-group">
                <label>Display:</label>
                <select
                  value={displayCount}
                  onChange={(e) => {
                    setDisplayCount(parseInt(e.target.value, 10));
                  }}
                  className="control-select"
                >
                  <option value={12}>12 per page</option>
                  <option value={24}>24 per page</option>
                  <option value={36}>36 per page</option>
                  <option value={48}>48 per page</option>
                </select>
              </div>
              <div className="control-group">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="control-select">
                  <option value="newest">Newest</option>
                  <option value="price-low">Price, low to high</option>
                  <option value="price-high">Price, high to low</option>
                  <option value="name">Name, A-Z</option>
                  <option value="brand">Brand</option>
                </select>
              </div>
              <div className="view-controls">
                <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>List</button>
                <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>Grid</button>
              </div>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="no-products"><p>No products available. Please try again later.</p></div>
          ) : (
            <div className={`products-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
              {displayedProducts.map(product => {
                // Debug each product
                console.log('Rendering product:', product);
                
                // Safely extract values with defaults
                const productId = product.id || product._id || Math.random();
                const productName = product.productname || product.name || 'Product Name Not Available';

                // const productBrand = product.brand || 'Unknown Brand';
                const productBrand = typeof product.brand === 'object' 
                  ? (product.brand.display_name || product.brand.name || 'Unknown Brand') 
                  : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand');
                const productPrice = parseFloat(product.price || product.current_price || 0);
                const productDiscountPrice = parseFloat(product.discountprice || 0);
                const productStock = product.stock || 'Out of Stock';

                // const productCategory = product.category || 'uncategorized';
                const productCategory = typeof product.category === 'object' 
                  ? (product.category.display_name || product.category.name || 'uncategorized') 
                  : (typeof product.category === 'string' ? product.category : 'uncategorized');
                const productImage = product.image || null;
                const productInfo = product.productinfo || product.description || '';
                
                // Calculate discount
                const hasDiscount = productDiscountPrice > 0 && productDiscountPrice < productPrice;
                const discountPercentage = hasDiscount 
                  ? Math.round(((productPrice - productDiscountPrice) / productPrice) * 100)
                  : 0;
                
                // Format price function
                const formatPrice = (price) => {
                  if (!price || isNaN(price)) return 'P 0.00';
                  return `P ${parseFloat(price).toFixed(2)}`;
                };
                
                return (
                  <div key={productId} className={`product-card ${viewMode === 'list' ? 'list-card' : 'grid-card'}`}>
                    <div className="product-image" style={{ background: 'transparent' }}>
                      {productImage ? (
                        <img
                          src={formatImageUrl(productImage)}
                          alt={productName}
                          className="product-img"
                          onClick={() => handleProductClick(product.id)} // ✅ ONLY ADD THIS LINE
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            cursor: 'pointer' // ✅ Add cursor pointer
                          }}
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.src = '/images/placeholder.jpg';
                          }}
                        />
                      ) : (
                        <div 
                          className="image-placeholder"
                          onClick={() => handleProductClick(product.id)} // ✅ ADD THIS LINE
                          style={{ cursor: 'pointer' }}
                        >
                          {/* {productBrand} */}
                                                  {productBrand}
                        </div>
                      )}
                      <button 
                        className="quick-view-btn" 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent image click
                          handleQuickView(product);
                        }}
                      >
                        Quick view
                      </button>
                      {discountPercentage > 0 && (
                        <div className="product-badge"><span className="discount-badge">-{discountPercentage}%</span></div>
                      )}
                    </div>

                    <div className="product-info">
                      <div className="product-brand">{productBrand}</div>
                      <h3 
                        className="product-name" 
                        title={productName}
                        onClick={() => handleProductClick(product.id)} // ✅ OPTIONAL: Make name clickable too
                        style={{ 
                          cursor: 'pointer', // ✅ Add cursor pointer
                          ...(viewMode === 'list' ? { 
                            backgroundColor: 'inherit', 
                            color: 'black',
                            padding: '0px 2px 20px 0px',
                          } : {})
                        }}
                      >
                        {productName}
                      </h3>

                      {viewMode === 'list' && productInfo && (
                        <p className="product-description">
                          {productInfo.length > 150 ? productInfo.substring(0, 150) + '...' : productInfo}
                        </p>
                      )}

                      <div className="product-pricing">
                        <span className="current-price">
                          {hasDiscount ? formatPrice(productDiscountPrice) : formatPrice(productPrice)}
                        </span>
                        {hasDiscount && (
                          <span className="original-price">{formatPrice(productPrice)}</span>
                        )}
                      </div>

                      <div className="stock-info">
                        <span className={`${productStock && productStock.toLowerCase().includes('out') ? 'out-of-stock' : 'in-stock'}`}>
                          {productStock && productStock.toLowerCase().includes('out') ? '✗ Out of Stock' : '✓ In Stock'}
                        </span>
                        <span className="product-category">{productCategory}</span>
                      </div>


                      <div className="product-actions">
                        <button 
                          className="add-to-cart-btn" 
                          onClick={() => handleAddToCart(product)}
                          disabled={productStock && productStock.toLowerCase().includes('out')}
                        >
                          {productStock && productStock.toLowerCase().includes('out') ? 'Out of Stock' : 'Add to cart'}
                        </button>
                        <button 
                          className="wishlist-btn" 
                          onClick={(e) => handleWishlistToggle(product, e)}
                          style={{
                            color: isInWishlist(productId) ? '#e74c3c' : '#666',
                            background: isInWishlist(productId) ? '#ffe6e6' : 'transparent',
                            border: isInWishlist(productId) ? '1px solid #e74c3c' : '1px solid #ddd'
                          }}
                        >
                          {isInWishlist(productId) ? '❤️' : '🤍'} {isInWishlist(productId) ? 'In Wishlist' : 'Wishlist'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isModalOpen && quickViewProduct && (
            <QuickViewModal 
              product={quickViewProduct} 
              isOpen={isModalOpen} 
              onClose={closeQuickView} 
              onAddToCart={handleAddToCart} 
              formatPrice={formatPrice} 
            />
          )}

          {totalProducts > displayCount && (
            <div className="pagination">
              <button className="load-more-btn" onClick={() => setDisplayCount(displayCount + 12)}>Load More Products</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;